/**
 * Central rules for "Select member" / team roster pickers:
 * - Team userType (or teamList Role) **Member** → viewer only sees themselves.
 * - **Manager**, **Admin**, or any other role → full team roster.
 *
 * Uses `teamDatabase.memberDetails.{uid}.userType` when present; otherwise
 * falls back to `membersDatabase` teamList `Role` from Redux when available.
 */

/**
 * @param {string | null | undefined} role
 * @returns {string}
 */
export function normalizeTeamRole(role) {
  if (role == null || typeof role !== 'string') return '';
  return role.trim().toLowerCase();
}

/**
 * Resolve teamList Role for the active team from Redux (team picker / session).
 * @param {object | null | undefined} teamState
 * @param {string | null | undefined} teamId
 * @returns {string | null}
 */
export function resolveTeamListRoleFromTeamState(teamState, teamId) {
  if (!teamId || !teamState) return null;
  const id = String(teamId);
  if (
    teamState.selectedTeamID === id ||
    teamState.active?.activeTeamid === id
  ) {
    const r =
      teamState.activeTeam?.Role ??
      teamState.active?.Role ??
      teamState.activeTeam?.role;
    if (r != null && String(r).trim() !== '') return String(r);
  }
  const rows = teamState.teams?.data;
  if (Array.isArray(rows)) {
    const row = rows.find((t) => {
      const tid = t?.activeTeamid ?? t?.teamId ?? t?.id;
      return tid != null && String(tid) === id;
    });
    if (row?.Role != null && String(row.Role).trim() !== '')
      return String(row.Role);
  }
  return null;
}

/**
 * Whether the current user should only see their own entry in member dropdowns.
 * @param {{ memberDetailsMap: Record<string, object>, currentUserId: string | null | undefined, teamListRole: string | null | undefined }} args
 * @returns {boolean}
 */
export function viewerSeesOnlySelfInMemberPicker({
  memberDetailsMap,
  currentUserId,
  teamListRole,
}) {
  if (!memberDetailsMap || !currentUserId) return false;
  const me = memberDetailsMap[currentUserId];
  const fromUserType = me?.userType != null ? String(me.userType) : '';
  if (fromUserType.trim() !== '') {
    return normalizeTeamRole(fromUserType) === 'member';
  }
  if (teamListRole != null && String(teamListRole).trim() !== '') {
    return normalizeTeamRole(teamListRole) === 'member';
  }
  return false;
}

/**
 * @param {Record<string, object> | null | undefined} memberDetailsMap
 * @param {string | null | undefined} currentUserId
 * @param {boolean} selfOnly
 * @returns {Record<string, object>}
 */
export function filterMemberDetailsForSelect(
  memberDetailsMap,
  currentUserId,
  selfOnly
) {
  if (
    !selfOnly ||
    !currentUserId ||
    !memberDetailsMap ||
    typeof memberDetailsMap !== 'object'
  ) {
    return memberDetailsMap && typeof memberDetailsMap === 'object'
      ? { ...memberDetailsMap }
      : {};
  }
  const self = memberDetailsMap[currentUserId];
  if (!self) return {};
  return { [currentUserId]: self };
}
